/**
 * Ride out a deploy.
 *
 * Every push restarts the server: migrations run, then it listens again. For a
 * second or two the proxy has nothing to talk to and answers 504 — and anyone
 * who clicked in that window sees a failure for something that is about to
 * work. Asking again a moment later turns it into a slightly slow page.
 *
 * This wraps window.fetch once, at startup, rather than editing the 240 call
 * sites across the app. A wrapper is a blunt instrument and worth being
 * deliberate about: it is why the rules below are narrow.
 *
 * ONLY idempotent requests are retried — GET and HEAD. A GET asked twice
 * returns the same thing. A POST asked twice can place the same order twice,
 * and a timeout is precisely the case where the first attempt may have
 * succeeded without the reply reaching us. A duplicate order is far worse than
 * an error message, so nothing with a body is ever repeated.
 */

const RETRY_STATUSES = new Set([502, 503, 504]);
const RETRY_DELAYS_MS = [400, 1200];   // about 1.6s of patience, then give up

function isIdempotent(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method =
    init?.method ??
    (typeof input === 'object' && 'method' in input ? (input as Request).method : 'GET');
  const m = String(method || 'GET').toUpperCase();
  return m === 'GET' || m === 'HEAD';
}

export function installRetryFetch(): void {
  // Guarded so a double install (fast refresh, a stray second call) cannot
  // nest the wrapper inside itself and multiply the retries.
  if ((window as any).__retryFetchInstalled) return;
  (window as any).__retryFetchInstalled = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const retryable = isIdempotent(input, init);
    let lastError: unknown;

    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      try {
        const res = await original(input, init);
        if (!RETRY_STATUSES.has(res.status) || !retryable || attempt === RETRY_DELAYS_MS.length) {
          return res;
        }
      } catch (err) {
        // The connection itself failed — refused mid-restart, or dropped.
        lastError = err;
        if (!retryable || attempt === RETRY_DELAYS_MS.length) throw err;
      }
      await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
    throw lastError ?? new Error('Request failed');
  };
}
